export const VULNERABLE_PASSENGER_POLICY = {
  id: 'vulnerable-passenger-policy',
  title: 'Vulnerable Passenger Policy',
  version: 'v1.0-2026-05-17',
  effectiveDate: '17/05/2026',
  audience: 'all',
  content: `VULNERABLE PASSENGER POLICY — RESERVO

Status: DRAFT — pending solicitor review

1. PURPOSE

This policy sets out how Reservo identifies and safeguards passengers who may, by reason of age, disability, medical condition, intoxication, language, or other circumstance, be at heightened risk during a trip.

2. SCOPE

This policy applies to all bookings facilitated through the Reservo platform and is binding on all Drivers and Reservo personnel.

3. WHO IS A VULNERABLE PASSENGER

3.1 For the purposes of this policy, a vulnerable passenger may include any person who:
  (a) is under 18 years of age (a "minor");
  (b) has a physical, sensory, intellectual, cognitive, or psychiatric disability;
  (c) has a serious medical condition that may require accommodation or attention during travel;
  (d) is significantly affected by alcohol or other substances;
  (e) has limited English proficiency or other communication barriers;
  (f) is escaping domestic or family violence or another safety emergency;
  (g) is travelling at an unusual hour from an unusual location in circumstances suggesting heightened risk;
  (h) is flagged by Reservo Operations as high-risk for any other reason.

3.2 Vulnerability may be:
  (a) self-declared by the passenger via their Rider profile;
  (b) declared by the Rider on behalf of co-riders;
  (c) flagged by Reservo Operations following a complaint, incident, or pattern review;
  (d) apparent on observation by the Driver during the trip.

4. SUPPORT CATEGORIES

4.1 Riders may indicate the following support needs on their profile or per booking:
  (a) accessibility need (wheelchair, mobility aid, walking frame, prosthetic);
  (b) guide or support animal;
  (c) communication support (sign language, low literacy, limited English);
  (d) cognitive or psychiatric support;
  (e) child seat or unaccompanied minor restriction;
  (f) emergency contact for trip notification;
  (g) specific dietary, medical, or environmental requirement.

4.2 Drivers receive these indicators at the time a booking is offered, before they accept.

5. DRIVER RESPONSIBILITIES

5.1 A Driver who accepts a booking with declared support needs commits to providing that support to the extent reasonably practicable.

5.2 Drivers must NOT:
  (a) refuse service on the basis of disability, age, gender identity, race, religion, or any other protected attribute;
  (b) refuse a guide or support animal;
  (c) demand additional payment for accessibility support;
  (d) display discomfort, impatience, or hostility toward a vulnerable passenger;
  (e) attempt to engage a vulnerable passenger in personal matters or social communication outside what is necessary for the trip.

5.3 Drivers MUST:
  (a) confirm the passenger's identity and intended destination calmly and in plain language;
  (b) assist with loading and unloading where physical limitations are evident or requested, taking reasonable care to avoid injury;
  (c) drive with extra care and at reasonable speeds when carrying frail or sensitive passengers;
  (d) avoid sudden movements, music, lighting, or temperature changes that could distress a sensory-sensitive passenger;
  (e) stop the trip safely and notify Reservo immediately if the passenger appears to be in medical distress.

6. UNACCOMPANIED MINORS

6.1 Unaccompanied minors are NOT permitted on the Reservo platform.

6.2 Minors may travel as passengers only when accompanied by an adult Rider over 18 years of age who has accepted the booking.

6.3 If a Driver arrives at a pickup and the passenger appears to be an unaccompanied minor, the Driver must:
  (a) not commence the trip;
  (b) report the situation to Reservo Operations immediately via the SOS feature or admin@reservo.au;
  (c) wait with the minor in a safe public place until an adult collects them or until Reservo or police arrive.

6.4 If a Driver suspects a minor is being trafficked, exploited, or abused, the Driver must:
  (a) not commence or, if already underway, safely terminate the trip;
  (b) contact NSW Police on 000 or 131 444;
  (c) report the matter to Reservo immediately.

7. INTOXICATED PASSENGERS

7.1 A passenger affected by alcohol or another substance may still be transported if their behaviour is safe.

7.2 A Driver may decline or terminate a trip if a passenger:
  (a) is unable to safely enter or exit the vehicle;
  (b) is at risk of vomiting or other behaviour likely to soil the vehicle;
  (c) is aggressive, threatening, or harassing;
  (d) is unconscious or unresponsive (in which case the Driver must call 000 first and Reservo Operations second).

7.3 Drivers must not abandon an intoxicated passenger in an unsafe location.

8. DOMESTIC AND FAMILY VIOLENCE

8.1 Reservo recognises that some passengers may be using the service to leave dangerous situations.

8.2 Drivers must:
  (a) treat such trips with discretion;
  (b) not disclose pickup or destination details to any third party;
  (c) follow any specific instructions provided in the booking;
  (d) escalate immediately to Reservo Operations and 000 if they witness or suspect violence.

9. RESERVO OPERATIONS RESPONSIBILITIES

9.1 Reservo Operations:
  (a) review high-risk flags on rider profiles and add them where required;
  (b) monitor SOS activations and incident reports for vulnerable-passenger patterns;
  (c) maintain a welfare-check protocol for trips that go off-route, exceed expected duration, or trigger SOS;
  (d) conduct training for Drivers on vulnerable-passenger handling at onboarding and annually;
  (e) cooperate with NSW Police and the Commissioner where vulnerable-passenger incidents arise.

10. PRIVACY

10.1 Vulnerability indicators are sensitive personal information under the Privacy Act 1988 (Cth).

10.2 Such information is:
  (a) collected only with the passenger's express consent;
  (b) shared only with the Driver accepting the relevant booking, and Reservo Operations;
  (c) retained per the Reservo Privacy Policy and Data Retention Policy.

11. COMPLAINTS

11.1 A passenger or third party who believes this policy has been breached may submit a complaint through the in-app complaints feature or to admin@reservo.au.

11.2 Complaints concerning vulnerable-passenger handling are triaged as priority and may be referred to the NSW Point to Point Transport Commissioner.

12. REVIEW

12.1 This policy is reviewed annually and after any significant vulnerable-passenger incident.

---
VERSION HISTORY
v1.0 — 17/05/2026 — Initial draft for solicitor review.

---
THIS DOCUMENT IS A DRAFT FOR PRE-LAUNCH REVIEW.`,
};